import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {Alert, Autocomplete, Box, Divider, Stack, Tab, Tabs, ToggleButton, ToggleButtonGroup} from "@mui/material";
import {useState} from "react";
import {ProfileService} from "crqlar-react-common/dist/services/profile/profile.service";
import {ProfileType} from "crqlar-react-common/dist/models/profile/profileType.model";
import {
    canSaveGuestAppUser,
    NewProfileDraft,
    ProfileLinkMode,
    profileFromDraft,
} from "./guestAppUserProfile.util";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import {GuestAppUserService} from "crqlar-react-common/dist/services/guestApp/guestAppUser.service";
import {GuestAppUser} from "crqlar-react-common/dist/models/guestApp/guestAppUser.model";
import Property from "crqlar-react-common/dist/components/Property";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import ProfileInfoRow from "crqlar-react-common/dist/components/ProfileInfoRow";
import SettingsEntityHeader from "../layout/SettingsEntityHeader";
import {isRequiredTextMissing} from "src/utils/requiredField.util";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {useQueryClient} from "react-query";
import {GUEST_APP_MODULE_QUERY_KEY} from "crqlar-react-common/dist/utils/hooks/guestApp/useGuestAppModule";
import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";
import {resolveTab} from "src/utils/tab.util";
import GuestAppPreview from "./GuestAppPreview";
import {appPreviewUrl} from "./guestAppPreview.util";

const GuestAppUserDetails = () => {

    useTrackScreenVisit('system guest app user details')

    const {t} = useTranslation()
    const {id} = useParams()
    const navigate = useNavigate()
    const readOnly = useReadOnly()
    const queryClient = useQueryClient()

    const isNew = !id || id === 'new'

    // The App preview tab exists only for an organization whose guest app runs on the web, which
    // is what its app_preview_url tag says. Everyone else keeps the single page they have today.
    const organization = useOrganization()
    const appUrl = appPreviewUrl(organization)
    const [searchParams, setSearchParams] = useSearchParams()
    const [profileMode, setProfileMode] = useState<ProfileLinkMode>('existing')
    const [profileDraft, setProfileDraft] = useState<NewProfileDraft>({})

    const guestAppUserData = useEntity<GuestAppUser>({
        get: async () => {
            if (id && id !== 'new') {
                return GuestAppUserService.getGuestAppUserById(id)
            } else {
                return {}
            }
        },
        // The profile is created first and its id carried onto the account, because an account
        // saved without one cannot ever sign in and cannot be repaired from this screen.
        create: async (guestAppUser) => {
            if (profileMode !== 'new') return GuestAppUserService.createGuestAppUser(guestAppUser)
            const profile = await ProfileService.createProfile(
                profileFromDraft(profileDraft, guestAppUser.email)
            )
            return GuestAppUserService.createGuestAppUser({...guestAppUser, profileId: profile.id})
        },
        update: GuestAppUserService.updateGuestAppUser,
        delete: (id) => GuestAppUserService.deleteGuestAppUser({id: [id]}),
        onDeleted: () => {
            queryClient.invalidateQueries([GUEST_APP_MODULE_QUERY_KEY])
            navigate('/system/guestAppUsers', {replace: true})
        },
        onSaved: (guestAppUser, wasCreateAction) => {
            if (wasCreateAction) {
                queryClient.invalidateQueries([GUEST_APP_MODULE_QUERY_KEY])
                navigate(`/system/guestAppUsers/${guestAppUser.id}`, {replace: true})
            }
        },
        isValid: (guestAppUser) => (
            canSaveGuestAppUser(guestAppUser, isNew, profileMode, profileDraft)
        ),
        cleanupBeforeSave: (guestAppUser) => {
            return {
                ...guestAppUser,
                email: guestAppUser.email?.trim()
            }
        }
    }, id)

    const guestAppUser = guestAppUserData.item
    const setGuestAppUser = guestAppUserData.setItem

    // Only once an account has actually loaded: an id in the address that matches nothing would
    // otherwise get a preview tab with no one to sign in as.
    const tabs = appUrl && !isNew && guestAppUser.id ? ['info', 'preview'] : ['info']
    const activeTab = resolveTab(searchParams.get('tab'), tabs, 'info')
    const emailMissing = isRequiredTextMissing(guestAppUserData, guestAppUser.email)

    const updateField = (field: keyof GuestAppUser, value: any) => {
        setGuestAppUser({...guestAppUser, [field]: value})
    }

    const sectionTitleSx = {
        fontSize: {xs: '18px', sm: '20px'},
        lineHeight: 1.25,
    }

    const fieldGroupGap = {xs: 1.5, sm: 1}

    const sectionDividerSx = {
        display: {xs: 'none', sm: 'block'},
    }

    const headerSection = (
        <SettingsEntityHeader
            title={guestAppUser.email || (id === 'new' ? t('New guest app user') : undefined)}
            data={guestAppUserData}
            back={() => navigate('/system/guestAppUsers')}
        />
    )

    const dangerZoneSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <DangerZone
                title={t('Delete guest app user')}
                description={t('This permanently deletes the guest app user. This action cannot be undone.')}
                buttonLabel={t('Delete')}
                loading={guestAppUserData.saving}
                disabled={readOnly}
                onDelete={() => guestAppUserData.removeNow()}
            />
            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {guestAppUser.updateDate?.toLocaleString() || '-'}
            </Typography>
        </Stack>
    )

    const basicInfoSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <Typography variant={'h5'} textAlign={'start'} sx={sectionTitleSx}>
                {t('Basic info')}
            </Typography>

            <Stack gap={fieldGroupGap}>
                <Property
                    title={t('Email')}
                    tooltip={t('Email address this guest app user uses to sign in or receive account communication.')}
                >
                    <TextField
                        id={'email'}
                        size={'small'}
                        type={'email'}
                        value={guestAppUser.email || ''}
                        onChange={(e) => updateField('email', e.target.value)}
                        fullWidth
                        required
                        error={emailMissing}
                    />
                </Property>

                <Property
                    title={t('Phone')}
                    tooltip={t('Optional phone number associated with this guest app user.')}
                >
                    <TextField
                        id={'phone'}
                        size={'small'}
                        type={'tel'}
                        value={guestAppUser.phone || ''}
                        onChange={(e) => updateField('phone', e.target.value)}
                        fullWidth
                    />
                </Property>

                <Property
                    title={t('Language')}
                    tooltip={t('Preferred language for this guest app user when localized content is available.')}
                >
                    <TextField
                        id={'language'}
                        size={'small'}
                        value={guestAppUser.language || ''}
                        onChange={(e) => updateField('language', e.target.value)}
                        fullWidth
                    />
                </Property>

                {isNew && (
                    <Property
                        title={t('Profile')}
                        tooltip={t('Every app user needs a profile. Without one the account receives a sign-in code and then fails to sign in.')}
                    >
                        <ToggleButtonGroup
                            exclusive
                            size={'small'}
                            value={profileMode}
                            onChange={(e, value) => value && setProfileMode(value)}
                        >
                            <ToggleButton value={'existing'}>{t('Link existing profile')}</ToggleButton>
                            <ToggleButton value={'new'}>{t('Create new profile')}</ToggleButton>
                        </ToggleButtonGroup>
                    </Property>
                )}

                {(!isNew || profileMode === 'existing') && (
                    <ProfileInfoRow
                        id={'profile'}
                        profileId={guestAppUser.profileId}
                        setProfileId={(profileId) => {
                            if (profileId) {
                                updateField('profileId', profileId)
                            }
                        }}
                        onDelete={() => {
                            updateField('profileId', undefined)
                        }}
                    />
                )}

                {isNew && profileMode === 'new' && (
                    <>
                        <Property title={t('First Name')}>
                            <TextField
                                id={'newProfileFirstName'}
                                size={'small'}
                                value={profileDraft.firstName || ''}
                                onChange={(e) => setProfileDraft({...profileDraft, firstName: e.target.value})}
                                fullWidth
                                required
                            />
                        </Property>

                        <Property title={t('Last Name')}>
                            <TextField
                                id={'newProfileLastName'}
                                size={'small'}
                                value={profileDraft.lastName || ''}
                                onChange={(e) => setProfileDraft({...profileDraft, lastName: e.target.value})}
                                fullWidth
                                required
                            />
                        </Property>

                        <Property
                            title={t('Profile type')}
                            tooltip={t('Employee unlocks the TEAM area in the app. Anything else behaves as a guest.')}
                        >
                            <Autocomplete
                                id={'newProfileType'}
                                options={Object.values(ProfileType)}
                                getOptionLabel={(option: any) => option ? t(`Profile type ${option}`) : ''}
                                value={profileDraft.profileTypeC ?? null}
                                fullWidth
                                onChange={(e, value) => setProfileDraft({
                                    ...profileDraft,
                                    profileTypeC: (value as ProfileType) ?? undefined,
                                })}
                                renderInput={(params) => (
                                    <TextField {...params} fullWidth size={'small'} required/>
                                )}
                            />
                        </Property>
                    </>
                )}

                {/* An account already in this state predates the requirement above. Say so rather
                    than let somebody wonder why the person cannot sign in. */}
                {!isNew && !guestAppUser.profileId && (
                    <Alert severity={'warning'}>
                        {t('This app user has no profile, so signing in will fail. Link a profile to fix it.')}
                    </Alert>
                )}

                {guestAppUser.lastActivity && (
                    <Property
                        title={t('Last Activity')}
                        tooltip={t('Most recent recorded activity for this guest app user.')}
                    >
                        <TextField
                            id={'lastActivity'}
                            size={'small'}
                            value={guestAppUser.lastActivity ? new Date(guestAppUser.lastActivity).toLocaleString() : ''}
                            fullWidth
                            disabled
                        />
                    </Property>
                )}
            </Stack>
        </Stack>
    )

    const guestAppUserDataUI = (
        <Stack
            sx={{
                width: '100%',
                maxWidth: '900px',
                mx: 'auto',
                gap: 6
            }}
        >
            {basicInfoSection}

            {
                !!guestAppUser.id &&
                <>
                    <Divider sx={sectionDividerSx}/>
                    {dangerZoneSection}
                </>
            }
        </Stack>
    )

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {headerSection}

            {tabs.length > 1 && (
                <Box sx={{width: '100%', maxWidth: '900px', mx: 'auto', mb: 4}}>
                    <Tabs
                        value={activeTab}
                        onChange={(e, tab) => setSearchParams({tab})}
                        aria-label={t('App user tabs')}
                        sx={{borderBottom: 1, borderColor: 'divider'}}
                    >
                        <Tab label={t('Basic info')} value={'info'}/>
                        <Tab label={t('App preview')} value={'preview'}/>
                    </Tabs>
                </Box>
            )}

            {activeTab === 'info' && guestAppUserDataUI}

            {activeTab === 'preview' && appUrl && guestAppUser.id && (
                <GuestAppPreview guestAppUser={guestAppUser} appUrl={appUrl}/>
            )}
        </Box>
    );
}

export default GuestAppUserDetails;
